import { Component, OnInit, Inject, PLATFORM_ID, ElementRef, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Title, Meta } from '@angular/platform-browser';
import { BlogService } from '../_services/blog.service';
import { BlogPost } from '../_models/blog-post';
import { ClientPartnerService } from '../_services/client-partner.service';
import { TestimonialService } from '../_services/testimonial.service';
import { TaxonomyService } from '../_services/taxonomy.service';
import { Offer, getPrice } from '../_models/offer';
import { environment } from '../../environments/environment';
import { API_URLS } from '../config/api.urls';

interface ValueProp {
    icon: string;
    title: string;
    desc: string;
    link: string;
    linkLabel: string;
    userType?: string;
}

interface Segment {
    title: string;
    desc: string;
    icon: string;
}

interface Stat {
    label: string;
    value: string;
    displayValue: string;
    target: number;
}

interface Process {
    step: string;
    title: string;
    desc: string;
}

@Component({
    selector: 'app-home-new',
    templateUrl: './home-new.component.html',
    styleUrls: ['./home-new.component.css'],
    standalone: false
})
export class HomeNewComponent implements OnInit {
    stars = [1, 2, 3, 4, 5];

    valueProps: ValueProp[] = [
        {
            icon: 'fa-seedling',
            title: 'home_new.for_farmers_title',
            desc: 'home_new.for_farmers_desc',
            link: '/signup?userType=seller',
            linkLabel: 'home_new.signup_label',
            userType: 'seller',
        },
        {
            icon: 'fa-shopping-cart',
            title: 'home_new.for_buyers_title',
            desc: 'home_new.for_buyers_desc',
            link: '/signup?userType=buyer',
            linkLabel: 'home_new.start_shopping_label',
            userType: 'buyer',
        },
        {
            icon: 'fa-truck',
            title: 'home_new.for_logistics_title',
            desc: 'home_new.for_logistics_desc',
            link: '/signup?userType=carrier',
            linkLabel: 'home_new.partner_label',
            userType: 'carrier',
        },
        {
            icon: 'fa-chart-line',
            title: 'home_new.market_prices_title',
            desc: 'home_new.market_prices_desc',
            link: '/market-prices',
            linkLabel: 'home_new.view_prices_label',
        },
        {
            icon: 'fa-cloud-sun',
            title: 'home_new.weather_title',
            desc: 'home_new.weather_desc',
            link: '/weather',
            linkLabel: 'home_new.view_forecast_label',
        },
        {
            icon: 'fa-users',
            title: 'home_new.experts_title',
            desc: 'home_new.experts_desc',
            link: '/experts',
            linkLabel: 'home_new.join_community_label',
        },
    ];

    trendingOffers: Offer[] = [];
    trendingLoading = true;

    segments: Segment[] = [
        {
            title: 'home_new.segment_institutional_title',
            desc: 'home_new.segment_institutional_desc',
            icon: 'fa-building',
        },
        {
            title: 'home_new.segment_producers_title',
            desc: 'home_new.segment_producers_desc',
            icon: 'fa-seedling',
        },
        {
            title: 'home_new.segment_logistics_title',
            desc: 'home_new.segment_logistics_desc',
            icon: 'fa-truck',
        },
    ];

    @ViewChild('statsBand') statsBand!: ElementRef;

    stats: Stat[] = [
        { label: 'home_new.stat_verified_suppliers', value: '578', displayValue: '0', target: 578 },
        { label: 'home_new.stat_total_products', value: '--', displayValue: '0', target: 0 },
        { label: 'home_new.stat_active_users', value: '--', displayValue: '0', target: 0 },
        { label: 'home_new.stat_regions_covered', value: 'home_new.stat_central_africa', displayValue: 'home_new.stat_central_africa', target: 0 },
    ];

    processes: Process[] = [
        { step: '01', title: 'home_new.step_browse_title', desc: 'home_new.step_browse_desc' },
        { step: '02', title: 'home_new.step_quote_title', desc: 'home_new.step_quote_desc' },
        { step: '03', title: 'home_new.step_escrow_title', desc: 'home_new.step_escrow_desc' },
        { step: '04', title: 'home_new.step_track_title', desc: 'home_new.step_track_desc' },
    ];

    blogPosts: BlogPost[] = [];
    blogLoading = true;

    partners: any[] = [];
    partnersLoading = true;

    testimonials: any[] = [];
    testimonialsLoading = true;
    countersStarted = false;

    constructor(
        private router: Router,
        private translateService: TranslateService,
        private titleService: Title,
        private meta: Meta,
        private blogService: BlogService,
        private clientPartnerService: ClientPartnerService,
        private testimonialService: TestimonialService,
        private taxonomyService: TaxonomyService,
        private http: HttpClient,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {}

    ngOnInit(): void {
        this.setMetaTags();
        this.loadMetrics();
        this.loadBlogPosts();
        this.loadPartners();
        this.loadTestimonials();
        this.loadTrendingOrders();
    }

    getHeadlineClass(line: number): string {
        const currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'fr';
        if (currentLang === 'en') {
            return line === 2 || line === 3 ? 'text-primary-green' : '';
        }
        return line === 1 || line === 3 ? 'text-primary-green' : '';
    }

    loadMetrics(): void {
        this.http.get<{ data: { totalUsers: number; totalProducts: number } }>(API_URLS.COMMON_METRICS).subscribe({
            next: (res) => {
                const usersStat = this.stats.find(s => s.label === 'home_new.stat_active_users');
                if (usersStat) { usersStat.target = res.data.totalUsers; usersStat.value = res.data.totalUsers.toLocaleString(); }
                const productsStat = this.stats.find(s => s.label === 'home_new.stat_total_products');
                if (productsStat) { productsStat.target = res.data.totalProducts; productsStat.value = res.data.totalProducts.toString(); }
                this.startCounters();
            },
            error: () => {
                const usersStat = this.stats.find(s => s.label === 'home_new.stat_active_users');
                if (usersStat) { usersStat.target = 850; usersStat.value = '850+'; }
                const productsStat = this.stats.find(s => s.label === 'home_new.stat_total_products');
                if (productsStat) { productsStat.target = 15000; productsStat.value = '15,000'; }
                this.startCounters();
            }
        });
    }

    startCounters(): void {
        const el = this.statsBand?.nativeElement;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.runCounters();
                observer.disconnect();
            }
        }, { threshold: 0.3 });
        observer.observe(el);
    }

    runCounters(): void {
        if (this.countersStarted) return;
        this.countersStarted = true;
        for (const stat of this.stats) {
            if (stat.target > 0) this.animateCount(stat);
        }
    }

    animateCount(stat: Stat): void {
        const target = stat.target;
        const duration = 1800;
        const start = performance.now();
        const step = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            stat.displayValue = Math.floor(eased * target).toLocaleString();
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }

    navigate(targetSignupType: string): void {
        if (isPlatformBrowser(this.platformId)) {
            const userJson = localStorage.getItem('currentUser');
            if (userJson) {
                const user = JSON.parse(userJson);
                const dashboard = user.role === 'ADMIN' ? '/admin/dashboard' : '/user/dashboard';
                this.router.navigateByUrl(dashboard);
            } else {
                this.router.navigate(['/signup'], { queryParams: { userType: targetSignupType } });
            }
        }
    }

    loadBlogPosts(): void {
        this.blogService.loadLatest(4).subscribe(
            (posts: BlogPost[]) => {
                this.blogPosts = posts;
                this.blogLoading = false;
            },
            () => {
                this.blogLoading = false;
            }
        );
    }

    loadPartners(): void {
        this.clientPartnerService.loadAll().subscribe(
            (response: any) => {
                this.partners = (response.data || response || []).map((partner: any) => ({
                    id: partner.id,
                    name: partner.clientPartnerName,
                    image_path: partner.logoPath ? environment.image_cdn_url + '/' + partner.logoPath : ''
                }));
                this.partnersLoading = false;
            },
            () => {
                this.partners = [];
                this.partnersLoading = false;
            }
        );
    }

    loadTestimonials(): void {
        this.testimonialService.loadAll().subscribe(
            (response: any) => {
                const items = response.data || response || [];
                this.testimonials = items.map((item: any) => ({
                    id: item.id,
                    author: item.displayName,
                    role: item.jobTitle && item.company ? item.jobTitle + ', ' + item.company : item.jobTitle || item.company || '',
                    comment: item.comment,
                    photo: item.profilePhotoPath ? environment.image_cdn_url + '/' + item.profilePhotoPath : ''
                }));
                this.testimonialsLoading = false;
            },
            () => {
                this.testimonials = [];
                this.testimonialsLoading = false;
            }
        );
    }

    loadTrendingOrders(): void {
        this.taxonomyService.getTaxonomyOffers(4, 1, 12).subscribe(
            (response: any) => {
                this.trendingOffers = response.data || [];
                this.trendingLoading = false;
            },
            () => {
                this.trendingOffers = [];
                this.trendingLoading = false;
            }
        );
    }

    getTrendingProductName(offer: Offer): string {
        const lang = this.translateService.currentLang || this.translateService.defaultLang || 'fr';
        return lang === 'fr' && offer.product?.productNameFr
            ? offer.product.productNameFr
            : offer.product?.name;
    }

    getTrendingImageSrc(offer: Offer): string {
        if (offer.defaultPicture && offer.defaultPicture.length > 0) {
            return environment.image_cdn_url + '/' + offer.defaultPicture + '?func=crop&width=280&height=280';
        }
        return offer.product && offer.product.picture
            ? environment.image_cdn_url + '/' + offer.product.picture
            : '/assets/images/default.jpg';
    }

    getTrendingPrice(offer: Offer): number {
        return getPrice(offer);
    }

    getPartnerLogoSrc(logoPath: string): string {
        return logoPath || '/assets/images/default.jpg';
    }

    getTestimonialPhotoSrc(photoPath: string): string {
        return photoPath || '/assets/images/default.jpg';
    }

    getBlogImageSrc(imagePath: string): string {
        return imagePath !== '' && imagePath !== null
            ? environment.image_cdn_url + '/' + imagePath + '?func=cropfit&width=366&dpr=1.75&quality=100'
            : '/assets/images/blog-img1_1.jpg';
    }

    getBlogExcerpt(body: string): string {
        if (!body) return '';
        const stripped = body.replace(/<[^>]+>/g, '');
        const txt = document.createElement('textarea');
        txt.innerHTML = stripped;
        const decoded = txt.value;
        return decoded.length > 150 ? decoded.substring(0, 150) + '...' : decoded;
    }

    private setMetaTags(): void {
        const title = 'eFarm.cm Marketplace - Fresh Farm Products Online';
        const description = "Africa's reliable digital marketplace and supply chain partner in agribusiness. Connecting farmers, buyers and logistics partners across Cameroon and beyond.";
        this.titleService.setTitle(title);
        this.meta.updateTag({ name: 'description', content: description });
        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:site_name', content: 'eFarm.cm Marketplace' });
        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: title });
        this.meta.updateTag({ name: 'twitter:description', content: description });
    }
}
